import { last } from "es-toolkit";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { Dropdown, type DropdownItem } from "@/components/sidebar/Dropdown";
import { insertNodeChildren } from "@/components/suggestions/utils";
import { preserveMathCursorPosition } from "@/components/tiptap/utils/preserveMathCursorPosition";
import { insertLabel } from "@/input/utils/labels";
import { cursorIsActive } from "@/models/CursorModel";
import { getFlattenOperatorsByType } from "@/models/nodes/operators";
import { displayHotkey, isKnownHotkey } from "@/utils/hotkeys";
import { twMerge } from "@/utils/twMerge";

import type { FlattenOperator } from "@/models/nodes/operators";
import type { SymbolNodeModel } from "@/models/nodes/SymbolNodeModel";
import type { LabelNodePosition } from "@corca/data";

function getLabelPosition(operator: FlattenOperator) {
  const position = last((operator.substitution?.applyTo || "").split(":"));
  return position as LabelNodePosition | undefined;
}

type Props = {
  node: SymbolNodeModel;
  className?: string;
  toggleButtonClassName?: string;
  toggleButtonWrapperClassName?: string;
};

export const SymbolFieldDropdown = observer(function SymbolFieldDropdown({
  node,
  className,
  toggleButtonClassName,
  toggleButtonWrapperClassName,
}: Props) {
  const { doc, labelNodes } = node;
  const t = useTranslations();
  const { cursor, selection } = doc.cursorContext();

  const operators = getFlattenOperatorsByType(["subscript", "superscript"]).filter((op) => {
    return op.renderPlacement?.includes("active-node");
  });

  if (operators.length === 0) {
    return null;
  }

  const handleFieldSelect = (operator: FlattenOperator) => {
    const { substitution } = operator;
    const position = getLabelPosition(operator);

    doc.transact(() => {
      if (!position || !cursorIsActive(cursor)) {
        return;
      }

      const activeLabel = labelNodes.find((label) => label.position === position);

      if (activeLabel) {
        doc.removeNode(activeLabel);

        if (!substitution?.insertChildren) {
          return;
        }
      }

      preserveMathCursorPosition(doc, () => {
        const inlineNode = insertLabel(node, position);

        if (inlineNode) {
          insertNodeChildren(doc, inlineNode, substitution?.insertChildren);
        }
      });
    }, "symbolFieldMenu:item:click");
  };

  const items: DropdownItem[] = operators.map((operator, i) => {
    const position = getLabelPosition(operator);
    const hotkey = `label:${position}`;

    const renderDelimiter = !!(
      !operator.substitution?.insertChildren && operators[i + 1]?.substitution?.insertChildren
    );

    const isActive = operator.substitution?.insertChildren
      ? false
      : !!labelNodes.find((label) => label.position === position);

    return {
      title: operator.intlName ? t(`suggestions.${operator.intlName}`) : "Operator",
      title2: operator.category?.[0] || "",
      hotkey: isKnownHotkey(hotkey) ? displayHotkey(hotkey) : "",
      icon: operator.icon ? <Icon name={operator.icon} /> : null,
      active: isActive,
      footer: () => (renderDelimiter ? <Sidebar.Delimiter className="mx-4 my-1" /> : undefined),
      onClick: () => handleFieldSelect(operator),
    };
  });

  const handleOpenChange = (open: boolean) => {
    if (open && node.parent && selection.isSelected(node.parent)) {
      selection.resetSelection();
      cursor.moveAfterNode(node.parent);
    }
  };

  return (
    <Dropdown
      className={className}
      onOpenChange={handleOpenChange}
      toggleButtonComponent={
        <Sidebar.ToggleButton
          as="div"
          icon={<Icon name="labels/RightBottomLabel" />}
          tooltipTitle={t("sidebar.actions.addIndex")}
          className={twMerge("min-h-full", toggleButtonClassName)}
          wrapperClassName={toggleButtonWrapperClassName}
        />
      }
      items={items}
    />
  );
});
